/// <amd-module name="Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/rejectManagementWizard/useInvoiceReasonCodeData"/>
import { ListItem, SortInfo } from "@medius/ui-controls";
import { useEffect, useMemo, useState } from "react";
import { getActiveRejectionActions, getActiveRejectManagementReasonCodes, getReasonRequiredUponRejection } from "./services";
import { IRejectManagementReasonCode, IInvoiceReasonCodeData, RejectManagementWizardType } from "./interfaces";
import { searchRejectManagementReasonCodes } from "./searching";

export const useInvoiceReasonCodeData = (operation : RejectManagementWizardType, hierarchyId: string): IInvoiceReasonCodeData => {
    const [sortInfo, setSortInfo] = useState<SortInfo | null>(null);
    const [{ page, pageSize }, setPageState] = useState({
        page: 0,
        pageSize: 25
    });
    const [searchTerm, setSearchTerm] = useState("");
    const [totalInvoiceReasonCodeCount, setTotalInvoiceReasonCodeCount] = useState(0);

    const changePageSize = (newPageSize: number) => {
        setPageState((prev) => ({
            ...prev,
            pageSize: newPageSize
        }));
    };

    const changePage = (newPage: number) => {
        setPageState((prev) => ({
            ...prev,
            page: newPage
        }));
    };

    const getOperationName = (type: RejectManagementWizardType) => {
        return type === RejectManagementWizardType.Delete ? 'Delete' : 'Invalidate';
    };

    const [allData, setAllData] = useState<IRejectManagementReasonCode[]>([]);
    const [actionOptions, setActionOptions] = useState<ListItem[]>([]);
    const [forceReasonCode, setForceReasonCode] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);

    useEffect(() => {
        setIsLoading(true);
        getActiveRejectManagementReasonCodes(hierarchyId)
            .then((result) => {
                let filtered = [
                    ...result.filter(r => r.operations?.includes(getOperationName(operation))),
                    ...result.filter(r => r.operations?.includes('All'))
                ];
                // Move 'Other' with isSystemReasonCode true to the end
                const others = filtered.filter(r => r.reasonCode?.trim().toLowerCase() === 'other' && r.isSystemReasonCode === true);
                filtered = filtered.filter(r => !(r.reasonCode?.trim().toLowerCase() === 'other' && r.isSystemReasonCode === true));
                filtered = [...filtered, ...others];
                setAllData(filtered);
                setIsError(false);
            })
            .catch(() => {
                setAllData([]);
                setIsError(true);
            })
            .finally(() => setIsLoading(false));

        getActiveRejectionActions(hierarchyId)
            .then((result) => { 
                setActionOptions(result.map(a => ({ value: String(a.id), text: a.code })));
                setIsError(false);
            })
            .catch(() => {
                setActionOptions([]);
                setIsError(true);
            })
            .finally(() => setIsLoading(false));

        const companyId = getCompanyIdByHierarchyId(hierarchyId);
        if(companyId === null){
            setIsError(true);
        }

        getReasonRequiredUponRejection(companyId)
            .then((result) => {
                setForceReasonCode(result);
                setIsError(false);
            })
            .catch(() => {
                setIsError(true);
            })
            .finally(() => setIsLoading(false));

    }, [operation, hierarchyId]);

    const pagedData = useMemo(() => {
    const data = [...searchRejectManagementReasonCodes(allData, searchTerm)];
    setTotalInvoiceReasonCodeCount(data.length);
    
    if (sortInfo?.columnKey) {
        const key = sortInfo.columnKey as keyof IRejectManagementReasonCode;

        data.sort((a, b) => {
        const aValue = a[key];
        const bValue = b[key];

        if (aValue === bValue) return 0;

        const isAsc = sortInfo.direction === "ascending";
        return aValue > bValue ? (isAsc ? 1 : -1) : (isAsc ? -1 : 1);
        });
    }

    const start = page * pageSize;
    return data.slice(start, start + pageSize);
    }, [allData, page, pageSize, sortInfo, searchTerm]);

    return {
        invoiceReasonCodes: pagedData,
        forceReasonCode: forceReasonCode,
        totalInvoiceReasonCodeCount: totalInvoiceReasonCodeCount,
        actions: actionOptions,
        isLoading: isLoading,
        isError: isError,
        paging: {
            currentPage: page,
            pageSize: pageSize,
            changePage: changePage,
            changePageSize: changePageSize
        },
        sorting: {
            sortInfo: sortInfo,
            changeSortInfo: setSortInfo
        },
        searching: {
            changeSearchTerm: (term: string) =>{
                changePage(0);
                setSearchTerm(term);
            }
        }
    };
};

const getCompanyIdByHierarchyId = (hierarchyId: string): number | null => {
    const parts = hierarchyId.split("/").filter(Boolean);
    const last = parts.pop();
    const id = Number(last);
    return isNaN(id) ? null : id;
};